import { CircleCheck, Lightbulb, type LucideIcon, Split } from "lucide-react";

import { getToolStep } from "@src/features/ai/utils/thought-process-steps";
import { isWorkLogToolCall } from "./conversationTools";

import { FlattenedPart } from "@src/features/ai/components/WorkflowEditor/execution/types";

/**
 * Figma "Atlas / Work-log Row" variants: an action Atlas took (a tool call) or
 * a thought it had (a reasoning part). The closing "Done" row is not a step —
 * the log appends it once the task is complete.
 */
export type WorkLogStepKind = "action" | "thought";

/**
 * Figma's three row glyphs, and only these: `ig-icon/split` for an action,
 * `ig-icon/lightbulb` for a thought, `ig-icon/circle-check` for the closing
 * row.
 */
export const WORK_LOG_ICON: Record<WorkLogStepKind | "done", LucideIcon> = {
  action: Split,
  thought: Lightbulb,
  done: CircleCheck,
};

export type WorkLogStep = {
  /** Stable across re-renders: the step's identity, which is also its dedupe key. */
  key: string;
  kind: WorkLogStepKind;
  label: string;
  /** The first part that produced this step, for the debug overlay. */
  part: FlattenedPart;
};

/** A reasoning row's label: Figma's "Thought about this step". */
export const WORK_LOG_THOUGHT_LABEL = "Thought about this step";

/** The header while no step is on screen yet, and whenever the list is open. */
export const WORK_LOG_WORKING_LABEL = "Thought process…";

/** The header once the task is complete. */
export const WORK_LOG_COMPLETE_LABEL = "Task completed";

/** The closing row of a completed log. */
export const WORK_LOG_DONE_LABEL = "Done";

const isPlaceholderReasoning = (text: string | undefined): boolean => {
  const trimmed = text?.trim();
  return (
    !trimmed ||
    trimmed === "Thinking..." ||
    trimmed.toLowerCase().includes("noop")
  );
};

/**
 * The step one part renders as, or `undefined` for a part the log does not
 * show: text, a placeholder thought, and a clarifying question once it has
 * been written — it is asked on its own card, never logged as work.
 */
function stepOf(
  item: FlattenedPart,
  conversationTools: string[] | undefined
): Omit<WorkLogStep, "key" | "part"> | undefined {
  const { part } = item;
  if (part.type === "dynamic-tool") {
    if (!isWorkLogToolCall(part, conversationTools)) {
      return undefined;
    }
    return {
      kind: "action",
      label: getToolStep(part.toolName, part.input).title,
    };
  }
  if (part.type === "reasoning") {
    if (isPlaceholderReasoning(part.text)) {
      return undefined;
    }
    return { kind: "thought", label: WORK_LOG_THOUGHT_LABEL };
  }
  return undefined;
}

const stepKey = (step: Pick<WorkLogStep, "kind" | "label">) =>
  `${step.kind}:${step.label}`;

/**
 * The rows of one work log: every step the parts describe, each at most once.
 *
 * A step is identified by what its row says, not by the call behind it — two
 * "Listing Products" calls that differ only in arguments the row never shows
 * are the same step to the merchant — so a repeat of a step already on the
 * list, adjacent or not, adds no second row. The list keeps the order steps
 * FIRST appeared in, which is the order the work started in.
 */
export function buildWorkLogSteps(
  parts: FlattenedPart[],
  conversationTools: string[] | undefined
): WorkLogStep[] {
  const steps: WorkLogStep[] = [];
  const seen = new Set<string>();
  for (const item of parts) {
    const step = stepOf(item, conversationTools);
    if (!step) {
      continue;
    }
    const key = stepKey(step);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    steps.push({ ...step, key, part: item });
  }
  return steps;
}

/**
 * The step Atlas is on now: the one the most recent step-producing part
 * describes, looked up in the `steps` already built from the same parts. That
 * can be a step already listed higher up — a repeat moves no row, but it is
 * still what Atlas is doing.
 */
export function currentWorkLogStep(
  parts: FlattenedPart[],
  steps: WorkLogStep[],
  conversationTools: string[] | undefined
): WorkLogStep | undefined {
  for (let index = parts.length - 1; index >= 0; index--) {
    const step = stepOf(parts[index], conversationTools);
    if (step) {
      const key = stepKey(step);
      return steps.find((candidate) => candidate.key === key);
    }
  }
  return undefined;
}

/**
 * What the log's header says (Figma "Atlas / Work-log" states):
 *   - complete — "Task completed", open or not;
 *   - working, open — "Thought process…", the list below shows the steps;
 *   - working, collapsed — the step Atlas is on, the same text its row would
 *     show, since the header is all the merchant can see. Before the first
 *     step lands there is none, so it reads "Thought process…".
 */
export function workLogHeaderLabel({
  inProgress,
  open,
  current,
}: {
  inProgress: boolean;
  open: boolean;
  current: WorkLogStep | undefined;
}): string {
  if (!inProgress) {
    return WORK_LOG_COMPLETE_LABEL;
  }
  if (open || !current) {
    return WORK_LOG_WORKING_LABEL;
  }
  return current.label;
}
